import { useState, useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { NFTCard, NFTCardSkeleton } from "@/components/marketplace/NFTCard";
import { MarketplaceNFT } from "@/hooks/useMarketplaceNFTs";
import { cn } from "@/lib/utils";

interface NFTSectionCarouselProps {
  title: string;
  icon: React.ReactNode;
  badge?: string;
  badgeVariant?: "default" | "secondary" | "outline";
  nfts: MarketplaceNFT[];
  loading?: boolean;
  showTrending?: boolean;
  maxItems?: number;
  onBuySuccess?: () => void;
  className?: string;
}

export function NFTSectionCarousel({
  title,
  icon,
  badge,
  badgeVariant = "secondary",
  nfts,
  loading,
  showTrending = false,
  maxItems = 8,
  onBuySuccess,
  className,
}: NFTSectionCarouselProps) {
  const displayNfts = nfts.slice(0, maxItems);
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  if (loading) {
    return (
      <Card className={cn("bg-card/80 backdrop-blur-sm border border-border/50", className)}>
        <CardHeader className="pb-2 pt-4">
          <CardTitle className="flex items-center gap-2 text-xl">
            <div className="p-2 rounded-lg gradient-primary">{icon}</div>
            <span className="text-primary">{title}</span>
            {badge && <Badge variant={badgeVariant} className="ml-auto">{badge}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-stretch">
            {Array.from({ length: 4 }).map((_, i) => (
              <NFTCardSkeleton key={i} />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (displayNfts.length === 0) {
    return null;
  }

  const showNavigation = displayNfts.length > 4;

  return (
    <Card className={cn("bg-card/80 backdrop-blur-sm border border-border/50", className)}>
      <CardHeader className="pb-2 pt-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <div className="p-2 rounded-lg gradient-primary">{icon}</div>
          <span className="text-primary">{title}</span>
          {badge && <Badge variant={badgeVariant} className="ml-auto">{badge}</Badge>}
        </CardTitle>
      </CardHeader>

      <CardContent className="pb-4">
        {showNavigation ? (
          <div className="relative group">
            <div ref={emblaRef} className="overflow-hidden">
              <div className="flex gap-4">
                {displayNfts.map((nft) => (
                  <div
                    key={nft.id}
                    className="flex-shrink-0 w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)]"
                  >
                    <NFTCard nft={nft} showTrending={showTrending} onBuySuccess={onBuySuccess} />
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop Navigation Arrows */}
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-10",
                "h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                "hover:bg-primary/10 disabled:opacity-0"
              )}
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-5 w-5 text-primary" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-10",
                "h-10 w-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg",
                "opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                "hover:bg-primary/10 disabled:opacity-0"
              )}
              onClick={scrollNext}
              disabled={!canScrollNext}
              aria-label="Next page"
            >
              <ChevronRight className="h-5 w-5 text-primary" />
            </Button>

            {/* Page Indicators */}
            {scrollSnaps.length > 1 && (
              <div className="flex justify-center items-center gap-2 pt-3">
                <span className="text-xs text-muted-foreground">
                  {selectedIndex + 1} / {scrollSnaps.length}
                </span>
                <div className="flex gap-1.5">
                  {scrollSnaps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => scrollTo(index)}
                      aria-label={`Go to page ${index + 1}`}
                      className={cn(
                        "w-2 h-2 rounded-full transition-all duration-200",
                        index === selectedIndex ? "bg-primary w-4" : "bg-primary/30 hover:bg-primary/50"
                      )}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-stretch">
            {displayNfts.map((nft) => (
              <NFTCard key={nft.id} nft={nft} showTrending={showTrending} onBuySuccess={onBuySuccess} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}